import { useState, useRef, useEffect } from "react";
import { Calendar as CalendarIcon, ChevronDown, ExternalLink } from "lucide-react";

// Add-to-calendar button. Generates Google/Yahoo web URLs + a client-side
// .ics file for Apple Calendar / Outlook so no backend endpoint is needed.
//
// event = { title, description, location, starts_at (ISO), ends_at (ISO, optional), url }

// ---------- ICS helpers -------------------------------------------------

/** ICS wants YYYYMMDDTHHmmssZ — UTC, no punctuation. */
function icsDate(iso) {
  const d = new Date(iso);
  const pad = (n) => n.toString().padStart(2, "0");
  return (
    d.getUTCFullYear().toString() +
    pad(d.getUTCMonth() + 1) +
    pad(d.getUTCDate()) +
    "T" +
    pad(d.getUTCHours()) +
    pad(d.getUTCMinutes()) +
    pad(d.getUTCSeconds()) +
    "Z"
  );
}

/** Escape ICS text — commas, semicolons and newlines need backslash-escapes. */
function icsEscape(s) {
  return (s || "")
    .replace(/\\/g, "\\\\")
    .replace(/\n/g, "\\n")
    .replace(/,/g, "\\,")
    .replace(/;/g, "\\;");
}

function buildIcs(event) {
  const start = icsDate(event.starts_at);
  // Default to a 2h event when no end time is provided
  const endIso = event.ends_at || new Date(new Date(event.starts_at).getTime() + 2 * 3600 * 1000).toISOString();
  const end = icsDate(endIso);
  const uid = `${event.uid || Math.random().toString(36).slice(2)}@servrevu.com`;
  const now = icsDate(new Date().toISOString());
  const url = event.url || "";
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Servrevu//Events//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${now}`,
    `DTSTART:${start}`,
    `DTEND:${end}`,
    `SUMMARY:${icsEscape(event.title)}`,
    event.location ? `LOCATION:${icsEscape(event.location)}` : null,
    event.description ? `DESCRIPTION:${icsEscape(event.description)}` : null,
    url ? `URL:${icsEscape(url)}` : null,
    "END:VEVENT",
    "END:VCALENDAR",
  ].filter(Boolean);
  return lines.join("\r\n");
}

function downloadIcs(event) {
  const blob = new Blob([buildIcs(event)], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${(event.title || "event").replace(/[^\w\s.-]/g, "").slice(0, 60).trim() || "event"}.ics`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ---------- Web-calendar URLs -----------------------------------------

/** Google Calendar template URL (opens the "Save event" screen). */
function googleUrl(event) {
  const endIso = event.ends_at || new Date(new Date(event.starts_at).getTime() + 2 * 3600 * 1000).toISOString();
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: event.title || "Event",
    dates: `${icsDate(event.starts_at)}/${icsDate(endIso)}`,
    details: `${event.description || ""}${event.url ? `\n\n${event.url}` : ""}`.trim(),
    location: event.location || "",
    sf: "true",
    output: "xml",
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/** Yahoo Calendar template URL. */
function yahooUrl(event) {
  const start = new Date(event.starts_at);
  const endIso = event.ends_at || new Date(start.getTime() + 2 * 3600 * 1000).toISOString();
  const end = new Date(endIso);
  const durMin = Math.max(15, Math.round((end - start) / 60000));
  const dur = `${Math.floor(durMin / 60).toString().padStart(2, "0")}${(durMin % 60).toString().padStart(2, "0")}`;
  const params = new URLSearchParams({
    v: "60",
    view: "d",
    type: "20",
    title: event.title || "Event",
    st: icsDate(event.starts_at),
    dur,
    desc: `${event.description || ""}${event.url ? `\n\n${event.url}` : ""}`.trim(),
    in_loc: event.location || "",
  });
  return `https://calendar.yahoo.com/?${params.toString()}`;
}

// ---------- Component --------------------------------------------------

export default function AddToCalendar({ event, className = "", testId = "add-to-calendar" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close when clicking outside — keeps the dropdown well-behaved.
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, [open]);

  if (!event?.starts_at) return null;
  const enriched = { ...event, url: event.url || window.location.href };

  return (
    <div ref={ref} className={`relative inline-block ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full inline-flex items-center justify-center gap-1.5 rounded-sm text-[13px] font-medium text-[#5C4E48] hover:text-[#B31E1E] px-3 py-2 border border-[#EAE3DE] hover:border-[#B31E1E]/40 bg-white"
        data-testid={testId}
        aria-expanded={open}
      >
        <CalendarIcon size={13} />
        Add to calendar
        <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-1 min-w-[220px] bg-white border border-[#EAE3DE] rounded-md shadow-lg overflow-hidden z-50"
          data-testid={`${testId}-menu`}
        >
          <a
            href={googleUrl(enriched)}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-3 py-2.5 text-[13px] text-[#1B1614] hover:bg-[#FAF5F2]"
            data-testid={`${testId}-google`}
            onClick={() => setOpen(false)}
          >
            <span className="w-5 text-center">📅</span>
            Google Calendar
            <ExternalLink size={11} className="ml-auto text-[#8B7B73]" />
          </a>
          <button
            type="button"
            onClick={() => { downloadIcs(enriched); setOpen(false); }}
            className="w-full text-left flex items-center gap-2 px-3 py-2.5 text-[13px] text-[#1B1614] hover:bg-[#FAF5F2] border-t border-[#EAE3DE]"
            data-testid={`${testId}-apple`}
          >
            <span className="w-5 text-center">🍎</span>
            Apple Calendar
            <span className="ml-auto text-[10px] font-mono text-[#8B7B73]">.ics</span>
          </button>
          <button
            type="button"
            onClick={() => { downloadIcs(enriched); setOpen(false); }}
            className="w-full text-left flex items-center gap-2 px-3 py-2.5 text-[13px] text-[#1B1614] hover:bg-[#FAF5F2] border-t border-[#EAE3DE]"
            data-testid={`${testId}-outlook`}
          >
            <span className="w-5 text-center">📆</span>
            Outlook
            <span className="ml-auto text-[10px] font-mono text-[#8B7B73]">.ics</span>
          </button>
          <a
            href={yahooUrl(enriched)}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-3 py-2.5 text-[13px] text-[#1B1614] hover:bg-[#FAF5F2] border-t border-[#EAE3DE]"
            data-testid={`${testId}-yahoo`}
            onClick={() => setOpen(false)}
          >
            <span className="w-5 text-center">🟣</span>
            Yahoo Calendar
            <ExternalLink size={11} className="ml-auto text-[#8B7B73]" />
          </a>
        </div>
      )}
    </div>
  );
}
